import AuthorRow from 'components/AuthorRow'
import ComponentLoader from 'components/ComponentLoader'
import Page from 'components/Page'
import PageEditButton from 'components/Page/Page.EditButton'
import { PageLoader } from 'components/Page/Page.Loader'
import { View } from 'components/View.js'
import {
  ARTICLE_MODEL_CLASS_NAME,
  ARTICLE_MODEL_KEY
} from 'models/daos/article/constants'
import { COMMENT_HTML_ANCHOR_PREFIX } from 'models/daos/comment/constants'
import { isEditableUserUtil } from 'models/daos/user/utils'
import { getSharePropsModelUtil } from 'models/utils/get-share-props'
import { ArticleContent } from 'pages/Article/Content'
import loadInstance from 'modules/redux/decorators/loadInstance'
import { getRelativeUrlReduxUtil } from 'modules/redux/utils/get-url'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import isLoadingReduxUtil from 'modules/redux/utils/is-loading'
import shouldHideCommentsRouteUtil from 'modules/routes/utils/should-hide-comments'
import { hasHashValueLocationUtil } from 'modules/router/utils'
import { getInstanceUserSectionSelector } from 'modules/section/selectors'
import getInstanceSectionSelector from 'modules/section/selectors/get-instance'
import { STYLES_LAZY_LOAD_DEFAULT_SMALL_ELEMENT_HEIGHT } from 'modules/styles/constants/index.cjs'
import getCurrentUserSelector from 'modules/users/selectors/get-current'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'
import PropTypes from 'prop-types'
import React from 'react'
import LazyLoad from 'react-lazyload'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import './index.scss'
import Tags from 'components/Tags'
import hasShareRouteUtil from 'modules/routes/utils/has-share'
import hasAuthorRowRouteUtil from 'modules/routes/utils/has-author-row'
import shouldHideTagsRouteUtil from 'modules/routes/utils/should-hide-tags'
import WithRouteAndMatch from 'components/WithRouteAndMatch'
import getNameReduxUtil from 'modules/redux/utils/get-name/index.js'
import { ROUTE_PATH } from 'modules/routes/constants/index.cjs'
import getClassNameReduxUtil from '@gymaholic/packages/src/modules/redux/utils/get-class-name/index.js'
import NotFound from 'components/NotFound/index.js'
import { hasErrorReduxUtil } from 'modules/redux/utils/legacy/has-error/index.js'

function loadSupplementary () {
  return import(/* webpackChunkName: "modules/articles/containers/Supplementary" */ 'pages/Article/Supplementary')
}

const ANALYTICS_EVENT_PREFIX = 'ARTICLE'

@loadInstance
class ArticlePageComponent extends React.Component {
  hasCommentHash () {
    return hasHashValueLocationUtil(
      this.props.location,
      COMMENT_HTML_ANCHOR_PREFIX
    )
  }

  renderArticle () {
    if (!this.props.instance) {
      return
    }
    return (
      <div className='article'>
        {this.renderContent()}
        {this.renderSupplementary()}
        {this.renderLoader()}
      </div>
    )
  }

  renderAuthor () {
    const { user } = this.props
    if (!user) {
      return
    }
    if (!hasAuthorRowRouteUtil(this.props.match.pathname)) {
      return <View mt={2} />
    }
    return (
      <AuthorRow value={user} />
    )
  }

  renderContent () {
    const { instance } = this.props
    const content = getValueReduxUtil(instance, 'content')

    if (!content && isLoadingReduxUtil(instance)) {
      return
    }

    if (
      !content ||
      hasErrorReduxUtil(instance)
    ) {
      return <NotFound />
    }

    const tags = getValueReduxUtil(instance, ARTICLE_MODEL_KEY.TAGS) || []
    return (
      <ArticleContent
        location={this.props.location}
        value={content}
        // hasCommentHash={this.hasCommentHash()}
        hasShareButtons={hasShareRouteUtil(this.props.match.pathname)}
        {...getSharePropsModelUtil(instance)}
      >
        {
          !shouldHideTagsRouteUtil(this.props.location.pathname) && (
            <Tags
              tags={tags.map(getNameReduxUtil)}
              analyticsEventPrefix={ANALYTICS_EVENT_PREFIX}
              urlPrefix={`${ROUTE_PATH.SEARCH}?q=`}
              useSlug={false}
            />
          )
        }
        {this.renderAuthor()}
      </ArticleContent>
    )
  }

  renderEditButton () {
    if (!isEditableUserUtil(this.props.currentUser, this.props.instance)) {
      return
    }
    return <PageEditButton url={getRelativeUrlReduxUtil(this.props.instance)} />
  }

  renderLoader () {
    const commentsKey = ARTICLE_MODEL_KEY.COMMENTS
    if (
      !isLoadingReduxUtil(this.props.instance) ||
      !getValueReduxUtil(this.props.instance.data, commentsKey)
    ) {
      return
    }
    return <PageLoader hasNoMargin />
  }

  renderSupplementary () {
    const hasCommentHash = this.hasCommentHash()
    const publishedAt = getValueReduxUtil(this.props.instance, ARTICLE_MODEL_KEY.PUBLISHED_AT)

    if (
      getClassNameReduxUtil(this.props.instance) !== ARTICLE_MODEL_CLASS_NAME ||
      (
        isLoadingReduxUtil(this.props.instance) &&
        !getValueReduxUtil(this.props.instance, ARTICLE_MODEL_KEY.RECOMMENDED)
      ) ||
      shouldHideCommentsRouteUtil(this.props.location.pathname) ||
      !publishedAt
    ) {
      return
    }

    const element = (
      <ComponentLoader
        loadComponent={loadSupplementary}
        instance={this.props.instance}
        route={this.props.route}
        location={this.props.location}
        match={this.props.match}
        hasCommentHash={hasCommentHash}
      />
    )

    if (hasCommentHash) {
      return element
    }

    return (
      <LazyLoad
        height={STYLES_LAZY_LOAD_DEFAULT_SMALL_ELEMENT_HEIGHT}
        offset={-1}
      >
        {element}
      </LazyLoad>
    )
  }

  render () {
    return (
      <Page value={this.props.instance}>
        {this.renderEditButton()}
        {this.renderArticle()}
      </Page>
    )
  }
}

ArticlePageComponent.propTypes = {
  dispatch: PropTypes.func.isRequired,
  instance: PropTypes.object,
  currentUser: PropTypes.object,
  user: PropTypes.object,
  location: PropTypes.object.isRequired,
  match: PropTypes.object,
  isSmallScreen: PropTypes.bool,
  route: PropTypes.object.isRequired
}

const select = () => createSelector(
  getCurrentUserSelector(),
  getInstanceUserSectionSelector(),
  getInstanceSectionSelector(),
  isSmallScreenViewportSelect,
  (
    currentUser,
    user,
    instance,
    isSmallScreen
  ) => (
    {
      currentUser,
      user,
      instance,
      isSmallScreen
    }
  )
)

const ArticlePage = WithRouteAndMatch(connect(select)(ArticlePageComponent))

export default ArticlePage
